<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      salesOrder: null,
      documents: [],
      showActionModal: false,
      clientAction: '',
      remarkByClient: '',
      actionErrors: {},
      submitted: false,
    };
  },

  mounted() {
    const public_token = this.$route.params.public_token;
    this.loadDrawingDocuments(public_token);
  },

  methods: {
    async loadDrawingDocuments(public_token) {
      this.setLoading(true);
      try {
        const response = await axios.get(`sales-orders/drawing-documents/${public_token}`);
        this.salesOrder = response.data.data;
        const allDocs = response.data.data.documents || [];
        this.documents = allDocs.filter(d => d.document_type === 'drawing_document');
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    isImage(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isPDF(fileType) {
      return fileType === 'application/pdf';
    },

    openActionModal(action) {
      this.clientAction = action;
      this.remarkByClient = '';
      this.actionErrors = {};
      this.showActionModal = true;
    },

    closeActionModal() {
      this.showActionModal = false;
      this.clientAction = '';
      this.remarkByClient = '';
      this.actionErrors = {};
    },

    async submitAction() {
      this.actionErrors = {};

      this.setLoading(true);
      try {
        await axios.post('sales-orders/action', {
          client_action: this.clientAction,
          public_token: this.$route.params.public_token,
          remark_by_client: this.remarkByClient.trim(),
        });

        this.showActionModal = false;
        this.salesOrder.client_action = this.clientAction;
        this.salesOrder.remark_by_client = this.remarkByClient.trim();
        this.submitted = true;
      } catch (error) {
        if (error.response?.data?.errors) {
          this.actionErrors = error.response.data.errors;
        } else {
          this.$swalToast(
            error.response?.data?.message || 'Failed to submit action', 
            'error'
          );
        }
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <div style="min-height:100vh;background:#f1f5f9;padding:24px 16px;">

    <!-- Header Card -->
    <div v-if="salesOrder" style="max-width:900px;margin:0 auto;">

      <!-- SO Info -->
      <div style="background:#fff;border-radius:14px;padding:20px 24px;margin-bottom:16px;box-shadow:0 2px 10px rgba(0,0,0,.06);">
        <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
          <div>
            <div style="font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;">Sales Order</div>
            <div style="font-size:20px;font-weight:800;color:#0f172a;">{{ salesOrder.so_number }}</div>
            <div style="font-size:13px;color:#64748b;margin-top:3px;">{{ salesOrder.name }}</div>
          </div>
          <!-- Already Actioned -->
          <div v-if="salesOrder.client_action === 'approved'"
            style="display:flex;align-items:center;gap:8px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:10px 16px;">
            <i class="ti ti-circle-check" style="color:#16a34a;font-size:20px;"></i>
            <div>
              <div style="font-size:12px;font-weight:700;color:#16a34a;">Drawing Approved</div>
              <div style="font-size:11px;color:#64748b;">{{ salesOrder.remark_by_client }}</div>
            </div>
          </div>
          <div v-else-if="salesOrder.client_action === 'rejected'"
            style="display:flex;align-items:center;gap:8px;background:#fff0f3;border:1px solid #fecdd3;border-radius:10px;padding:10px 16px;">
            <i class="ti ti-circle-x" style="color:#f43f5e;font-size:20px;"></i>
            <div>
              <div style="font-size:12px;font-weight:700;color:#f43f5e;">Drawing Rejected</div>
              <div style="font-size:11px;color:#64748b;">{{ salesOrder.remark_by_client }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents -->
      <div style="background:#fff;border-radius:14px;padding:20px 24px;margin-bottom:16px;box-shadow:0 2px 10px rgba(0,0,0,.06);">
        <div style="font-size:13px;font-weight:700;color:#0f172a;margin-bottom:16px;display:flex;align-items:center;gap:8px;">
          <i class="ti ti-files" style="color:#3b82f6;font-size:16px;"></i>
          Drawing Documents ({{ documents.length }})
        </div>

        <!-- No Documents -->
        <div v-if="!documents.length" 
          style="text-align:center;padding:40px;color:#94a3b8;">
          <i class="ti ti-file-off" style="font-size:36px;display:block;margin-bottom:8px;opacity:.4;"></i>
          No drawing documents uploaded yet.
        </div>

        <!-- Documents List -->
        <div v-for="(doc, index) in documents" :key="doc.id" 
          style="margin-bottom:20px;border:1.5px solid #e2e8f0;border-radius:12px;overflow:hidden;">
          
          <!-- Doc Header -->
          <div style="background:#f8fafc;padding:10px 14px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #e2e8f0;">
            <div style="width:28px;height:28px;background:#eff6ff;border-radius:7px;display:flex;align-items:center;justify-content:center;">
              <i :class="isImage(doc.file_type) ? 'ti ti-photo' : 'ti ti-file-type-pdf'" 
                :style="isImage(doc.file_type) ? 'color:#3b82f6;' : 'color:#ef4444;'"
                style="font-size:14px;"></i>
            </div>
            <div style="flex:1;">
              <div style="font-size:12px;font-weight:600;color:#0f172a;">
                {{ doc.name || 'Document ' + (index + 1) }}
              </div>
              <div style="font-size:10px;color:#94a3b8;">{{ doc.document_type }}</div>
            </div>
            <a :href="doc.document_url" target="_blank"
              style="font-size:11px;font-weight:600;color:#2563eb;text-decoration:none;display:flex;align-items:center;gap:4px;">
              <i class="ti ti-external-link" style="font-size:12px;"></i> Open
            </a>
          </div>

          <!-- Image Preview -->
          <div v-if="isImage(doc.file_type)" style="padding:12px;background:#fff;text-align:center;">
            <img :src="doc.document_url" 
              style="max-width:100%;max-height:500px;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.08);" 
              :alt="doc.name" />
          </div>

          <!-- PDF Preview -->
          <div v-else-if="isPDF(doc.file_type)" style="background:#fff;">
            <iframe :src="doc.document_url" 
              style="width:100%;height:500px;border:none;" 
              :title="doc.name">
            </iframe>
          </div>

          <!-- Other File -->
          <div v-else style="padding:20px;text-align:center;color:#64748b;font-size:13px;">
            <i class="ti ti-file" style="font-size:28px;display:block;margin-bottom:6px;opacity:.4;"></i>
            Preview not available.
            <a :href="doc.document_url" target="_blank" 
              style="display:block;margin-top:8px;color:#2563eb;font-weight:600;">
              Download File
            </a>
          </div>

        </div>
      </div>

      <!-- Action Buttons -->
      <div v-if="!salesOrder.client_action"
        style="background:#fff;border-radius:14px;padding:20px 24px;box-shadow:0 2px 10px rgba(0,0,0,.06);display:flex;align-items:center;justify-content:flex-end;gap:12px;">
        <button @click="openActionModal('rejected')"
          style="display:inline-flex;align-items:center;gap:6px;padding:10px 20px;background:#fff0f3;border:1.5px solid #fecdd3;border-radius:9px;color:#f43f5e;font-size:13px;font-weight:600;cursor:pointer;">
          <i class="ti ti-x"></i> Reject
        </button>
        <button @click="openActionModal('approved')"
          style="display:inline-flex;align-items:center;gap:6px;padding:10px 20px;background:#16a34a;border:1.5px solid #16a34a;border-radius:9px;color:#fff;font-size:13px;font-weight:600;cursor:pointer;">
          <i class="ti ti-check"></i> Approve
        </button>
      </div>

    </div>

    <!-- Action Modal -->
    <div class="modal fade" :class="{ show: showActionModal }"
      :style="showActionModal ? 'display:block;z-index:1060;' : 'display:none'">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content" style="border:none;border-radius:14px;overflow:hidden;">
          <div class="modal-header" style="padding:14px 18px;border-bottom:1px solid #f0f1f5;background:#f8f9fb;">
            <h6 class="fw-bold mb-0" style="font-size:14px;">
              {{ clientAction === 'approved' ? '✅ Approve Drawing' : '❌ Reject Drawing' }}
            </h6>
            <button type="button" class="btn-close" @click="closeActionModal"></button>
          </div>
          <div class="modal-body" style="padding:20px;">
            <div class="mb-3">
              <label style="font-size:12px;font-weight:600;color:#475569;margin-bottom:6px;display:block;">
                Remark 
              </label>
              <textarea class="form-control" rows="3" v-model="remarkByClient"
                placeholder="Enter your remark (min 10 characters)..."
                style="font-size:13px;"></textarea>
              <div v-if="actionErrors.remark" 
                style="font-size:11px;color:#f43f5e;margin-top:4px;">
                {{ actionErrors.remark }}
              </div>
              <div v-if="actionErrors.remark_by_client" 
                style="font-size:11px;color:#f43f5e;margin-top:4px;">
                {{ actionErrors.remark_by_client[0] }}
              </div>
            </div>
          </div>
          <div class="modal-footer" style="padding:12px 18px;border-top:1px solid #f0f1f5;">
            <button type="button" class="btn btn-sm btn-light" @click="closeActionModal">
              Cancel
            </button>
            <button type="button" class="btn btn-sm"
              :class="clientAction === 'approved' ? 'btn-success' : 'btn-danger'"
              @click="submitAction">
              <i :class="clientAction === 'approved' ? 'ti ti-check' : 'ti ti-x'" class="me-1"></i>
              {{ clientAction === 'approved' ? 'Approve' : 'Reject' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showActionModal" 
      class="modal-backdrop fade show" 
      style="z-index:1055;" 
      @click="closeActionModal">
    </div>

  </div>
</template>